import { useRef, useState, useEffect } from 'react';
import {
  BLUES_PALETTE, REDS_PALETTE, GREENS_PALETTE, buildPaletteFromPrimary,
} from '../../charts/shared/palettes';
import {
  deploymentProfile,
  EXTERNAL_PRIMARY_COLORS,
} from '../../config/deploymentProfile';

const PICKER_PALETTES = deploymentProfile.isExternal
  ? EXTERNAL_PRIMARY_COLORS.map(({ value, label }) => ({
      label,
      colors: buildPaletteFromPrimary(value),
    }))
  : [
      { label: 'Blues', colors: BLUES_PALETTE },
      { label: 'Reds', colors: REDS_PALETTE },
      { label: 'Greens', colors: GREENS_PALETTE },
    ];
const PICKER_EXTRAS = deploymentProfile.isExternal ? [] : ['#6B7280', '#1E1E1E'];

function ColorPickerPopover({ anchorEl, currentColor, onSelect, onClose, ui, paletteColors, assignedColors }) {
  const popoverRef = useRef(null);
  const [hexInput, setHexInput] = useState(currentColor || '');
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!anchorEl) setPos(null);
    else setPos(true);
  }, [anchorEl]);

  useEffect(() => {
    function handleOutside(e) {
      if (e.target.type === 'color') return;
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          anchorEl && !anchorEl.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [onClose, anchorEl]);

  const handleHexSubmit = () => {
    const cleaned = hexInput.trim();
    const hex = cleaned.startsWith('#') ? cleaned : `#${cleaned}`;
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
      onSelect(hex.toUpperCase());
    }
  };

  const allPaletteColors = new Set(PICKER_PALETTES.flatMap(p => p.colors));
  const uniqueAssigned = assignedColors
    ? [...new Set(assignedColors)].filter(c => c && !allPaletteColors.has(c) && !PICKER_EXTRAS.includes(c))
    : [];

  if (!pos) return null;

  const swatchBtn = (color, key) => (
    <button
      key={key}
      onClick={() => onSelect(color)}
      className="w-7 h-7 rounded-sm"
      style={{
        backgroundColor: color,
        outline: currentColor === color ? `2px solid ${ui.text}` : 'none',
        outlineOffset: 1,
      }}
      title={color}
    />
  );

  const validHex = /^#?[0-9a-fA-F]{6}$/.test(hexInput.trim());
  const previewColor = validHex
    ? (hexInput.trim().startsWith('#') ? hexInput.trim() : `#${hexInput.trim()}`)
    : null;

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 border shadow-lg rounded-md"
      style={{
        backgroundColor: ui.bg,
        borderColor: ui.border,
        top: '50%',
        left: 264,
        transform: 'translateY(-50%)',
        width: 212,
        padding: 10,
      }}
    >
      {deploymentProfile.isExternal && EXTERNAL_PRIMARY_COLORS.map((colorOption) => {
        const palette = buildPaletteFromPrimary(colorOption.value);
        return (
          <div key={colorOption.value} className="mb-2">
            <div className="text-[10px] mb-1 font-medium" style={{ color: ui.textSecondary }}>{colorOption.label}</div>
            <div className="flex gap-1">
              <button
                onClick={() => onSelect(colorOption.value)}
                className="w-5 h-5 rounded-sm border flex-shrink-0"
                style={{
                  backgroundColor: colorOption.value,
                  outline: currentColor === colorOption.value ? `2px solid ${ui.text}` : 'none',
                  outlineOffset: 1,
                  borderColor: ui.borderSecondary,
                }}
                title={colorOption.value}
              />
              {palette.map((c, i) => (
                <button
                  key={i}
                  onClick={() => onSelect(c)}
                  className="w-5 h-5 rounded-sm"
                  style={{
                    backgroundColor: c,
                    outline: currentColor === c ? `2px solid ${ui.text}` : 'none',
                    outlineOffset: 1,
                  }}
                  title={c}
                />
              ))}
            </div>
          </div>
        );
      })}

      {!deploymentProfile.isExternal && PICKER_PALETTES.map(({ label, colors }) => (
        <div key={label} className="mb-2">
          <div className="text-[10px] mb-1 font-medium" style={{ color: ui.textSecondary }}>{label}</div>
          <div className="flex gap-1.5">
            {colors.map((c, i) => swatchBtn(c, `${label}-${i}`))}
          </div>
        </div>
      ))}

      {!deploymentProfile.isExternal && PICKER_EXTRAS.length > 0 && (
        <div className="flex gap-1.5 mb-2">
          {PICKER_EXTRAS.map((c, i) => swatchBtn(c, `extra-${i}`))}
        </div>
      )}

      {uniqueAssigned.length > 0 && (
        <div className="mb-2 pt-1" style={{ borderTop: `1px solid ${ui.borderSecondary}` }}>
          <div className="text-[10px] mb-1 font-medium" style={{ color: ui.textSecondary }}>In Use</div>
          <div className="flex flex-wrap gap-1.5">
            {uniqueAssigned.map((c, i) => swatchBtn(c, `used-${i}`))}
          </div>
        </div>
      )}

      <div className="pt-1" style={{ borderTop: `1px solid ${ui.borderSecondary}` }}>
        <div className="text-[10px] mb-1 font-medium" style={{ color: ui.textSecondary }}>Custom</div>
        <div className="flex gap-1 items-center">
          <label style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
            <div
              className="w-7 h-7 rounded-sm border"
              style={{ backgroundColor: previewColor || '#ffffff', borderColor: ui.borderSecondary }}
            />
            <input
              type="color"
              value={(previewColor || '#4c6ee6').toLowerCase()}
              onChange={(e) => setHexInput(e.target.value.toUpperCase())}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
            />
          </label>
          <input
            type="text"
            value={hexInput}
            onChange={(e) => setHexInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleHexSubmit()}
            placeholder="#4C6EE6"
            className="flex-1 text-xs border px-1.5 py-1 rounded-sm"
            style={{ borderColor: ui.borderSecondary, color: ui.text, backgroundColor: ui.bg, minWidth: 0 }}
            maxLength={7}
          />
          <button
            onClick={handleHexSubmit}
            className="text-xs border px-2 py-1 rounded-sm ui-hover"
            style={{ borderColor: ui.borderSecondary, color: ui.text }}
          >
            OK
          </button>
        </div>
        {deploymentProfile.isExternal && previewColor && (
          <div className="flex gap-1 mt-1.5 flex-wrap">
            {buildPaletteFromPrimary(previewColor).map((c, i) => (
              <button
                key={i}
                onClick={() => onSelect(c)}
                className="w-5 h-5 rounded-sm"
                style={{
                  backgroundColor: c,
                  outline: currentColor === c ? `2px solid ${ui.text}` : 'none',
                  outlineOffset: 1,
                }}
                title={c}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ColorPickerPopover;
